import LayerMenuPanel from './LayerMenuPanel';
import { on } from '../../utils/EventsBus';
import { CustomEvents } from '../../utils/CustomEvents';
import { trapFocusOnModal, removeTrapFocusOnModal } from '../../utils/TrapFocusOnModalUtils';

export default class MenuItem {
  get CLASSNAMES() {
    return {
      active: 'active',
      activeLayersContainer: 'active-layers',
      divider: 'header-divider',
    };
  }
  constructor(itemEl, layersContainerEl) {
    this.item = itemEl;
    this.panel = null;
    this.layersContainer = layersContainerEl;
    this.header = document.querySelector('header');
    this.keyboardToggleBtn = this.item.querySelector('.c-m-h__menu-item__keyboard-toggle');
    
    // save reference for accessibility
    this.item._menuItemInstance = this;
    
    if (this.item) {
      this.init();
    }

    this.$on = on;
  }
  init() {
    try {
      this.checkForPanel();
    } catch (e) {
      console.error(e);
    }
  }

  checkForPanel() {
    if (!this.layersContainer) {
      throw `No layers container declared at constructor`;
    }
    if (
      this.item.dataset.panelTarget &&
      this.item.dataset.panelTarget.length > 0
    ) {
      let target = this.item.dataset.panelTarget;
      let panelEl = this.layersContainer.querySelector(
        ` [data-menu-panel="${target}"]`
      );
      if (panelEl) {
        this.panel = new LayerMenuPanel(panelEl);
        this.startListener();
        this.initKeyboardNavigation();
      }
    }
  }

  initKeyboardNavigation() {
    if (!this.keyboardToggleBtn || !this.panel) return;

    // Gestione navigazione da tastiera per il pulsante toggle
    this.keyboardToggleBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        if (this.panel.isActive) {
          this.hidePanel();
          this.keyboardToggleBtn.setAttribute('aria-expanded', 'false');
        } else {
          this.showPanel();
          this.keyboardToggleBtn.setAttribute('aria-expanded', 'true');
          // Accessibility: Setup handler for Tab after opening the layer 
          this.#setupTabHandlerForOpenLayer();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.hidePanel();
        this.keyboardToggleBtn.setAttribute('aria-expanded', 'false');
        this.keyboardToggleBtn.focus();
      }
    });

    // Gestione focus in entrata sul menu item
    this.item.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        // Tab normale: se il panel è chiuso e c'è un pulsante keyboard, vai su quello
        if (!this.panel.isActive && this.keyboardToggleBtn && document.body.classList.contains('using-keyboard')) {
          const currentTarget = e.target;
          const linkElement = this.item.querySelector('.c-m-h__menu-item__desktop');

          if (currentTarget === linkElement) {
            e.preventDefault();
            this.keyboardToggleBtn.focus();
          }
        }
      }
    });

    // Gestione ESC all'interno del panel
    if (this.panel.element) {
      this.panel.element.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.hidePanel();
          this.keyboardToggleBtn.setAttribute('aria-expanded', 'false');
          this.keyboardToggleBtn.focus();
        }
      });

      // Gestione del pulsante back per il trap focus
      const backBtn = this.panel.element.querySelector('[data-close-panel]');
      if (backBtn) {
        backBtn.addEventListener('click', () => {
          setTimeout(() => {
            if (this.keyboardToggleBtn && document.body.classList.contains('using-keyboard')) {
              this.keyboardToggleBtn.focus();
            }
          }, 50);
        });
      }
    }
  }

  getFirstFocusableElement() {
    if (!this.panel.element) return null;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])'
    ].join(', ');

    const focusableElements = Array.from(this.panel.element.querySelectorAll(focusableSelectors))
      .filter(el => {
        return el.offsetWidth > 0 &&
          el.offsetHeight > 0 &&
          !el.hidden &&
          getComputedStyle(el).visibility !== 'hidden';
      });

    return focusableElements.length > 0 ? focusableElements[0] : null;
  }

  #setupTrapFocus() {
    if (!this.panel.element) return;

    // Rimuovi eventuali trap focus precedenti
    this.#removeTrapFocus();

    // Usa la utility per il trap focus
    const trapFocusHandler = trapFocusOnModal(this.panel.element);

    if (!trapFocusHandler) return;

    // Salva il riferimento per rimuoverlo successivamente
    this.panel.element._trapFocusHandler = trapFocusHandler;

    // Aggiungi handler per ESC
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hidePanel();
        this.keyboardToggleBtn.setAttribute('aria-expanded', 'false');
        this.keyboardToggleBtn.focus();
      }
    };

    this.panel.element._escapeHandler = escapeHandler;
    this.panel.element.addEventListener('keydown', escapeHandler);
  }

  #removeTrapFocus() {
    if (!this.panel.element) return;

    if (this.panel.element._trapFocusHandler) {
      removeTrapFocusOnModal(this.panel.element, this.panel.element._trapFocusHandler);
      delete this.panel.element._trapFocusHandler;
    }

    if (this.panel.element._escapeHandler) {
      this.panel.element.removeEventListener('keydown', this.panel.element._escapeHandler);
      delete this.panel.element._escapeHandler;
    }
  }

  #setupTabHandlerForOpenLayer() {
    if (!this.keyboardToggleBtn || !this.panel) return;
    this.#removeTabHandler();

    // Handler to manage Tab key when layer is open
    const tabHandler = (e) => {
      if (e.key === 'Tab' && !e.shiftKey && this.panel.isActive) {
        e.preventDefault();
        // Move focus to the first element in the layer and activate trap focus
        const firstFocusable = this.getFirstFocusableElement();
        if (firstFocusable) {
          firstFocusable.focus();
          setTimeout(() => {
            this.#setupTrapFocus();
          }, 50);
        }
        // Remove this handler after first use
        this.#removeTabHandler();
      }
    };

    this.keyboardToggleBtn.addEventListener('keydown', tabHandler);
    this.keyboardToggleBtn._tabHandler = tabHandler;
  }

  #removeTabHandler() {
    if (this.keyboardToggleBtn && this.keyboardToggleBtn._tabHandler) {
      this.keyboardToggleBtn.removeEventListener('keydown', this.keyboardToggleBtn._tabHandler);
      delete this.keyboardToggleBtn._tabHandler;
    }
  }

  isDesktop() {
    return window.matchMedia('(min-width:1024px)').matches;
  }
  startListener() {
    // check if is desktop
    if (this.isDesktop()) {
      this.item.addEventListener('mouseenter', e => {
        e.stopPropagation();
        e.preventDefault();
        if (this.isDesktop()) {
          // add timeout to avoid immediate open when search is also open
          this.hoverTimeout = setTimeout(() => { this.showPanel() }, 300);
        }
      });
      this.item.addEventListener('mouseleave', e => {
        e.stopPropagation();
        e.preventDefault();
        if (this.isDesktop()) {
          // clear timeout on enter, don't open
          clearTimeout(this.hoverTimeout);
          if (!this.panel.checkChild(e.relatedTarget)) {
            this.hidePanel();
          }
        }
      });
    } else {
      // mobile
      let detectTap;
      this.item.addEventListener('touchstart', e => {
        detectTap = true; // Detects all touch events
      });
      this.item.addEventListener('touchmove', e => {
        detectTap = false; // Excludes the scroll events from touch events
      });
      this.item.addEventListener('touchend', e => {
        e.preventDefault();
        if (e.type == "click") detectTap = true; // Detects click events
        if (detectTap) {
          // Here you can write the function or codes you want to execute on tap
          this.showPanel();
        }
      });
      if (!('ontouchstart' in window)) {
        this.item.addEventListener('click', e => {
          e.preventDefault();
          this.showPanel();
        });
      }
      let ctx = this;
      on(CustomEvents.MENU_EVENTS.showMenu, () => {
        if (!ctx.isDesktop()) {
          const location = window.location.href;
          const href = ctx.item.querySelector('a').href;
          if (location.includes(href)) {
            this.showPanel();
          }
        }
      })
    }
  }
  on(event, callback) {
    if (!this.panel) {
      return;
    }
    this.item.addEventListener(event, callback);
  }
  setActive(bool = true) {
    if (typeof bool !== 'boolean') {
      return;
    }
    this.item.classList.toggle(this.CLASSNAMES.active, bool);
  }
  showPanel() {
    if (!this.panel || this.panel.isActive) {
      return;
    }

    // Check if another layer is already open and close it
    const activeMenuItem = document.querySelector('.c-m-h__menu-item.active');
    if (activeMenuItem && activeMenuItem !== this.item) {
      // Find the instance of the active MenuItem and close its panel
      const activeMenuItemComponent = activeMenuItem._menuItemInstance;
      if (activeMenuItemComponent && activeMenuItemComponent.hidePanel) {
        activeMenuItemComponent.hidePanel();
      }
    }

    this.setActive();

    // Rendi il panel accessibile rimuovendo aria-hidden
    if (this.panel.panelElement) {
      this.panel.panelElement.removeAttribute('aria-hidden');
    }

    this.panel.showLayer();
    this.header.classList.add(this.CLASSNAMES.divider);
    this.togglePanelsContainer(this.panel.isActive);

    // Aggiorna attributi di accessibilità
    if (this.keyboardToggleBtn) {
      this.keyboardToggleBtn.setAttribute('aria-expanded', 'true');
    }

    // Attiva trap focus solo se si sta navigando da tastiera
    if (document.body.classList.contains('using-keyboard')) {
      setTimeout(() => {
        this.#setupTrapFocus();
      }, 50);
    }

    this.panel.onLeave(e => {
      if (e.relatedTarget === this.item || (this.keyboardToggleBtn && e.relatedTarget === this.keyboardToggleBtn)) {
        return;
      }
      this.hidePanel();
    });
    this.panel.whenBack(() => {
      this.hidePanel();
    });
  }

  hidePanel() {
    if (!this.panel) {
      return;
    }
    this.setActive(false);

    // Rimuovi il trap focus
    this.#removeTrapFocus();

    // Remove tab handler
    this.#removeTabHandler();

    // Make panel inaccessible by adding aria-hidden
    if (this.panel.panelElement) {
      this.panel.panelElement.setAttribute('aria-hidden', 'true');
    }

    this.panel.hideLayer();

    // Aggiorna attributi di accessibilità
    if (this.keyboardToggleBtn) {
      this.keyboardToggleBtn.setAttribute('aria-expanded', 'false');
    }

    if (!this.panel.isActive) {
      this.togglePanelsContainer(this.panel.isActive);
    }
  }
  togglePanelsContainer(bool = true) {
    if (typeof bool !== 'boolean') {
      return;
    }
    this.layersContainer.classList.toggle(this.CLASSNAMES.activeLayersContainer, bool);
  }
}
